import { useEffect, useState, useContext } from 'react';
import { Modal, Form, Row, Col, Button, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import SingleDatePicker from '../../../../components/common/SingleDatePicker/SingleDatePicker';
import TimeSlotSlider from '../../common/TimeSlotSlider';
import AutoCompleteInput from '../../../../components/common/AutoComplete/AutoCompleteInput';
import { reactSelectDark } from '../../../../styles/reactSelectDarkTheme';
import { ConfigContext } from '../../../../contexts/ConfigContext';

import {
  useViewBooking,
  useUpdateBooking,
  useGetServicePeriods,
  useGetAvailableTimeSlots
} from '../../../../APIs/BookingManagement/booking';
import { useGetRestaurantHolidays, useGetRestaurantOutlets } from '../../../../APIs/common';
import { notify } from '@common/NotificationService';
import './EditBookingModal.css';
import { reactSelectLight } from '../../../../styles/reactSelectLightTheme';

function useClickOutside(ref, callback) {
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) callback();
    };

    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, callback]);
}

// Normalize time format helper function (handle "13:30" vs "13:30:00")
function normalizeTime(time) {
  if (!time) return '';
  // Remove seconds if present and ensure proper format
  const parts = time.split(':');
  if (parts.length >= 2) {
    const hours = parts[0].padStart(2, '0');
    const minutes = parts[1].padStart(2, '0');
    return `${hours}:${minutes}`;
  }
  return time;
}

export default function EditBookingModal({ show, onHide, id, refresh }) {
  const config = useContext(ConfigContext);

  const { booking: bookingData, isLoading, refresh: viewRefresh } = useViewBooking(id);

  const [city, setCity] = useState(null);
  const [selectedCityId, setSelectedCityId] = useState(null);

  const [location, setLocation] = useState(null);
  const [locationOptions, setLocationOptions] = useState([]);

  const [date, setDate] = useState(null);
  const [holidays, setHolidays] = useState([]);

  const [guestCount, setGuestCount] = useState('');

  const [servicePeriod, setServicePeriod] = useState(null);
  const [servicePeriodOptions, setServicePeriodOptions] = useState([]);

  const [amenities, setAmenities] = useState([]);
  const [amenitiesOptions, setAmenitiesOptions] = useState([]);

  const [timeSlots, setTimeSlots] = useState([]);
  const [timeSlot, setTimeSlot] = useState(null);

  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [adminComment, setAdminComment] = useState('');
  const [modeOfBooking, setModeOfBooking] = useState('manual');

  const [saving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const [selectedSlotMeta, setSelectedSlotMeta] = useState(null);
  const [originalGuestCount, setOriginalGuestCount] = useState(0);
  const [isSynced, setIsSynced] = useState(false);

  // Only fetch outlets if we have a city ID and don't already have location from booking
  const shouldFetchOutlets = selectedCityId && !bookingData?.location;
  const { outlets, isLoading: isLocationLoading } = useGetRestaurantOutlets(shouldFetchOutlets ? { location_id: selectedCityId } : {});

  const { getRestaurantHolidays } = useGetRestaurantHolidays();
  const { getServicePeriods, isMutating: isServiceLoading } = useGetServicePeriods();
  const { getAvailableTimeSlots, isMutating: isSlotLoading } = useGetAvailableTimeSlots();
  const { updateBooking } = useUpdateBooking();

  // Update location options when outlets change
  useEffect(() => {
    if (outlets && outlets.length > 0) {
      const mapped = outlets.map((o) => ({
        value: o.id,
        label: o.name
      }));

      setLocationOptions((prev) => {
        const same = prev.length === mapped.length && prev.every((p, i) => p.value === mapped[i].value);
        return same ? prev : mapped;
      });
    }
  }, [outlets]);

  const stripAuPrefix = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.startsWith('61') ? cleaned.slice(2) : cleaned;
  };

  const formatAuPhone = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.startsWith('61') ? cleaned : `61${cleaned}`;
  };

  // Populate form when booking data loads
  useEffect(() => {
    if (!bookingData) return;

    // Set city - API only returns city name, not ID
    if (bookingData.city) {
      setCity({ label: bookingData.city, value: bookingData.city });
    } else {
      setCity(null);
    }

    // Set location - add it directly to options if not already there
    if (bookingData.location) {
      const locOption = { value: bookingData.location.id, label: bookingData.location.name };
      setLocation(locOption);

      // Add to location options if not already present
      setLocationOptions((prev) => {
        if (prev.some((p) => p.value === locOption.value)) return prev;
        return [locOption, ...prev];
      });
    }

    // Set date (convert from YYYY-MM-DD to DD/MM/YYYY)
    if (bookingData.booking_date) {
      const [year, month, day] = bookingData.booking_date.split('-');
      setDate(`${day}/${month}/${year}`);
    }

    // Set guest count
    if (bookingData.no_of_guests) {
      setGuestCount(bookingData.no_of_guests);
    }

    // Set service period
    if (bookingData.service_period) {
      setServicePeriod({ value: bookingData.service_period.id, label: bookingData.service_period.name });
    }

    // Set amenities
    if (bookingData.amenities && Array.isArray(bookingData.amenities)) {
      setAmenities(
        bookingData.amenities.map((a) => ({
          value: a.id || a,
          label: a.name || a
        }))
      );
    }

    // Time slot will be set after timeSlots are loaded (see useEffect below)

    // Set customer info
    if (bookingData.customer) {
      setCustomerName(bookingData.customer.name || '');
      // setCustomerPhone(bookingData.customer.phone || '');
      setCustomerPhone(stripAuPrefix(bookingData.customer.phone));

      setCustomerEmail(bookingData.customer.email || '');
    }

    // Set admin comment
    if (bookingData.internal_notes) {
      setAdminComment(bookingData.internal_notes);
    }

    // Set mode of booking
    if (bookingData.mode_of_booking) {
      setModeOfBooking(bookingData.mode_of_booking);
    }

    if (bookingData?.no_of_guests) {
      setGuestCount(String(bookingData.no_of_guests));
      setOriginalGuestCount(Number(bookingData.no_of_guests));
    }
  }, [bookingData]);

  // Load holidays when location changes
  useEffect(() => {
    if (location?.value) {
      getRestaurantHolidays(location.value).then((res) => {
        setHolidays(res.holidays || []);
      });
    }
  }, [location]);

  // useEffect(() => {
  //   if (!selectedSlotMeta || !guestCount) return;

  //   if (guestCount > selectedSlotMeta.availableSeats) {
  //     setErrors((prev) => ({
  //       ...prev,
  //       guestCount: `Only ${selectedSlotMeta.availableSeats} seats available for this slot`
  //     }));
  //   } else {
  //     setErrors((prev) => ({ ...prev, guestCount: undefined }));
  //   }
  // }, [selectedSlotMeta, guestCount]);

  // useEffect(() => {
  //   if (!selectedSlotMeta || !guestCount) return;

  //   const guestsNum = Number(guestCount);
  //   const maxAllowed = selectedSlotMeta.availableSeats + originalGuestCount;

  //   if (guestsNum > maxAllowed) {
  //     setErrors((prev) => ({
  //       ...prev,
  //       guestCount: `Only ${maxAllowed} seats available for this slot`
  //     }));
  //   } else {
  //     setErrors((prev) => ({ ...prev, guestCount: undefined }));
  //   }
  // }, [selectedSlotMeta, guestCount, originalGuestCount]);

  useEffect(() => {
    if (!selectedSlotMeta || !guestCount) return;

    const guestsNum = Number(guestCount);

    const maxAllowed = isSynced ? selectedSlotMeta.availableSeats : selectedSlotMeta.availableSeats + originalGuestCount;

    if (guestsNum > maxAllowed) {
      setErrors((prev) => ({
        ...prev,
        guestCount: `Only ${maxAllowed} seats available for this slot`
      }));
    } else {
      setErrors((prev) => ({ ...prev, guestCount: undefined }));
    }
  }, [selectedSlotMeta, guestCount, originalGuestCount, isSynced]);

  useEffect(() => {
    if (!show) {
      // Only reset when modal is closed
      setLocation(null);
      setDate(null);
      setGuestCount('');
      setServicePeriod(null);
      setAmenities([]);
      setTimeSlots([]);
      setTimeSlot(null);
      setSelectedSlotMeta(null);
      setErrors({});
      setIsSynced(false);
    }
  }, [show]);

  // Load service periods when location, date, or guest count changes
  useEffect(() => {
    if (location?.value && date && guestCount > 0) {
      getServicePeriods({
        location_id: location.value,
        date,
        no_of_guests: guestCount
      }).then((res) => {
        setServicePeriodOptions(
          res.servicePeriods.map((sp) => ({
            value: sp.id,
            label: sp.name
          }))
        );

        setAmenitiesOptions(
          res.preferences.map((p) => ({
            value: p.id,
            label: p.name
          }))
        );
      });
    }
  }, [location, date, guestCount]);

  const loadTimeSlots = async () => {
    const res = await getAvailableTimeSlots({
      location_id: location.value,
      date,
      service_period_id: servicePeriod.value,
      no_of_guests: guestCount
    });

    // const slots = (res.timeSlots || []).map((slot) => ({
    //   label: slot.time,
    //   value: slot.time_24h
    // }));

    // const slots = (res.timeSlots || []).map((slot) => ({
    //   label: slot.time,
    //   value: slot.time_24h,
    //   availableSeats: slot.available_seats,
    //   isFullyBooked: slot.is_fully_booked
    // }));

    const slots = (res.timeSlots || []).map((slot) => ({
      label: slot.time,
      value: slot.time_24h,
      availableSeats: slot.available_seats,
      isFullyBooked: slot.is_fully_booked
    }));

    setTimeSlots(slots);

    if (bookingData?.booking_time) {
      const normalized = normalizeTime(bookingData.booking_time);
      const matched = slots.find((s) => normalizeTime(s.value) === normalized);

      // if (matched) setTimeSlot(matched.label);
      if (matched) {
        setTimeSlot(matched.value);
        setSelectedSlotMeta(matched);
      }
    }
  };

  useEffect(() => {
    if (!bookingData) return;

    if (location?.value && date && guestCount > 0 && servicePeriod?.value) {
      loadTimeSlots();
    }
  }, [bookingData, location, date, guestCount, servicePeriod]);

  // Match booking time to loaded time slots (fallback if not matched during load)
  useEffect(() => {
    // Only match if we have booking time, time slots are loaded, and time slot is not already set
    if (bookingData?.booking_time && timeSlots.length > 0 && !timeSlot) {
      const bookingTime24 = bookingData.booking_time;
      const normalizedBookingTime = normalizeTime(bookingTime24);
      const matchedSlot = timeSlots.find((s) => {
        const normalizedSlotTime = normalizeTime(s.value);
        return normalizedSlotTime === normalizedBookingTime;
      });

      // if (matchedSlot) {
      //   setTimeSlot(matchedSlot.label);
      // }
      if (matchedSlot) {
        setTimeSlot(matchedSlot.value);
        setSelectedSlotMeta(matchedSlot);
      }
    }
  }, [timeSlots, bookingData?.booking_time, timeSlot]);

  const handleGuestChange = async (e) => {
    const value = e.target.value;

    // allow clearing input
    if (value === '') {
      setGuestCount('');
      setErrors((prev) => ({ ...prev, guestCount: undefined }));
      setIsSynced(false);
      return;
    }

    // allow only digits
    if (!/^\d+$/.test(value)) return;

    setGuestCount(value);
    setIsSynced(false);

    setServicePeriod(null);
    setAmenities([]);
    setTimeSlots([]);
    setTimeSlot(null);
    setSelectedSlotMeta(null);

    const guestsNum = Number(value);

    if (location?.value && date && guestsNum > 0) {
      const res = await getServicePeriods({
        location_id: location.value,
        date,
        no_of_guests: guestsNum
      });

      setServicePeriodOptions(
        res.servicePeriods.map((sp) => ({
          value: sp.id,
          label: sp.name
        }))
      );

      setAmenitiesOptions(
        res.preferences.map((p) => ({
          value: p.id,
          label: p.name
        }))
      );
    }
  };

  const handleServiceOrAmenitiesSelected = async () => {
    setTimeSlots([]);
    setTimeSlot(null);
    setSelectedSlotMeta(null);

    if (location?.value && date && guestCount > 0 && servicePeriod?.value) {
      const res = await getAvailableTimeSlots({
        location_id: location.value,
        date,
        service_period_id: servicePeriod.value,
        no_of_guests: guestCount
      });

      setTimeSlots(
        (res.timeSlots || []).map((slot) => ({
          label: slot.time,
          value: slot.time_24h,
          availableSeats: slot.available_seats,
          isFullyBooked: slot.is_fully_booked
        }))
      );
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const guestsNum = Number(guestCount);

    if (!city) newErrors.city = 'City is required';
    if (!location) newErrors.location = 'Location is required';
    if (!date) newErrors.date = 'Date is required';

    if (!guestCount || guestsNum <= 0) {
      newErrors.guestCount = 'Enter a valid number of guests';
    }
    // if (!guestCount || guestCount <= 0) newErrors.guestCount = 'Enter a valid number of guests';

    // if (selectedSlotMeta) {
    //   const maxAllowed = selectedSlotMeta.availableSeats + originalGuestCount;

    //   if (guestsNum > maxAllowed) {
    //     newErrors.guestCount = `Only ${maxAllowed} seats available for the selected slot`;
    //   }
    // }

    // if (selectedSlotMeta && guestCount > selectedSlotMeta.availableSeats) {
    //   newErrors.guestCount = `Only ${selectedSlotMeta.availableSeats} seats available for the selected slot`;
    // }

    if (selectedSlotMeta) {
      const maxAllowed = isSynced ? selectedSlotMeta.availableSeats : selectedSlotMeta.availableSeats + originalGuestCount;

      if (guestsNum > maxAllowed) {
        newErrors.guestCount = `Only ${maxAllowed} seats available for the selected slot`;
      }
    }

    if (!servicePeriod) newErrors.servicePeriod = 'Service period is required';
    // if (amenities.length === 0) newErrors.amenities = 'Select at least one amenity';

    if (bookingData?.mode_of_booking !== 'online' && !modeOfBooking) {
      newErrors.modeOfBooking = 'Mode of booking is required';
    }
    if (!timeSlot) newErrors.timeSlot = 'Select a time slot';

    if (!customerName.trim()) newErrors.customerName = 'Customer name is required';

    if (!customerPhone.trim()) {
      newErrors.customerPhone = 'Phone number is required';
    } else if (!/^[2-9]\d{8}$/.test(customerPhone.trim().replace(/[\s\-]/g, ''))) {
      newErrors.customerPhone = 'Enter 9 digits after +61 (e.g. 412 345 678)';
    }

    // Email is optional, but if provided, it must be valid
    if (customerEmail.trim() && !customerEmail.match(/^\S+@\S+\.\S+$/)) {
      newErrors.customerEmail = 'Enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    // Validate form before saving
    if (!validateForm()) {
      return;
    }

    // Convert date from DD/MM/YYYY to YYYY-MM-DD
    let formattedDate = date;
    if (date && date.includes('/')) {
      const [day, month, year] = date.split('/');
      formattedDate = `${year}-${month}-${day}`;
    }

    // Convert time slot from 12h to 24h format
    let time24h = timeSlot;
    if (timeSlot && timeSlot.includes(' ')) {
      const [time, ampm] = timeSlot.split(' ');
      const [hours, minutes = '00'] = time.split(':');
      let hour24 = parseInt(hours, 10);
      if (ampm === 'PM' && hour24 !== 12) hour24 += 12;
      if (ampm === 'AM' && hour24 === 12) hour24 = 0;
      time24h = `${hour24.toString().padStart(2, '0')}:${minutes}`;
    } else if (timeSlot && timeSlot.includes(':')) {
      // Already in 24h format, use as is
      time24h = timeSlot;
    }

    const payload = {
      location_id: Number(location?.value),
      service_period_id: servicePeriod?.value,
      date: formattedDate,
      time: time24h,
      no_of_guests: guestCount,
      customer_name: customerName,
      // customer_phone: customerPhone,
      customer_phone: formatAuPhone(customerPhone),

      customer_email: customerEmail,
      amenities: amenities.map((a) => a.value),
      internal_notes: adminComment,
      booking_status: bookingData?.booking_status || 'confirmed',
      mode_of_booking: modeOfBooking
    };

    setIsSaving(true);
    try {
      const res = await updateBooking(id, payload);
      if (res.success) {
        notify.success(res.message || 'Booking updated successfully', 'edit');
        setOriginalGuestCount(Number(guestCount));
        setIsSynced(true);
        setSelectedSlotMeta(null);

        await viewRefresh();
        await refresh();
        onHide(true);
      } else {
        notify.error(res.message || 'Failed to update booking', 'edit');
      }
    } catch (err) {
      notify.error(err?.response?.data?.message || 'Something went wrong', 'edit');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={() => onHide(false)} size="lg" centered className="edit-booking-modal">
      <Modal.Header closeButton>
        <Modal.Title>Edit Booking</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isLoading ? (
          <div className="py-5 d-flex justify-content-center align-items-center">
            <Spinner animation="border" role="status" />
          </div>
        ) : (
          <>
            <Row className="mb-3">
              <Col lg={3} md={6} sm={12} className="mb-3 mb-md-0">
                <Form.Label>
                  City <span className="color-danger">*</span>
                </Form.Label>
                <AutoCompleteInput
                  value={city?.label || ''}
                  apiEndpoint="/restaurant-location/get-location"
                  placeholder="Search City"
                  onChange={(text) => setCity({ label: text, value: text })}
                  onSelect={(item) => {
                    setCity({ label: item.name, value: item.id });
                    setSelectedCityId(item.id);
                    setLocation(null);
                  }}
                />
                {errors.city && <div className="text-danger small">{errors.city}</div>}
              </Col>

              <Col lg={3} md={6} sm={12} className="mb-3 mb-md-0">
                <Form.Label>
                  Location <span className="color-danger">*</span>
                </Form.Label>
                <Select
                  options={locationOptions}
                  value={location}
                  onChange={async (sel) => {
                    setLocation(sel);
                    setDate(null);
                    setGuestCount('');
                    setServicePeriod(null);
                    setAmenities([]);
                    setTimeSlots([]);
                    setTimeSlot(null);

                    // Load holidays
                    const res = await getRestaurantHolidays(sel.value);
                    setHolidays(res.holidays || []);
                  }}
                  placeholder="Select Location"
                  isDisabled={!selectedCityId && !bookingData?.location}
                  isLoading={isLocationLoading}
                  styles={config.mode === 'dark' ? reactSelectDark : reactSelectLight}
                />
                {errors.location && <div className="text-danger small">{errors.location}</div>}
              </Col>

              <Col lg={3} md={6} sm={12} className="mb-3 mb-md-0">
                <Form.Label>
                  Date <span className="color-danger">*</span>
                </Form.Label>
                <SingleDatePicker
                  disabled={!location}
                  value={date}
                  holidays={holidays.map((h) => h.date)}
                  onSelect={(selectedDate) => {
                    setDate(selectedDate);
                    setGuestCount('');
                    setServicePeriod(null);
                    setAmenities([]);
                    setTimeSlots([]);
                    setTimeSlot(null);
                  }}
                  useClickOutside={useClickOutside}
                />
                {errors.date && <div className="text-danger small">{errors.date}</div>}
              </Col>

              <Col lg={3} md={6} sm={12}>
                <Form.Label>
                  No. of Guests <span className="color-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  value={guestCount}
                  onChange={handleGuestChange}
                  disabled={!location || !date}
                  placeholder="Enter guests"
                />
                {errors.guestCount && <div className="text-danger small">{errors.guestCount}</div>}
              </Col>
            </Row>

            <Row className="mb-3">
              <Col lg={4} md={6} sm={12} className="mb-3 mb-md-0">
                <Form.Label>
                  Service Period <span className="color-danger">*</span>
                </Form.Label>
                <Select
                  options={servicePeriodOptions}
                  value={servicePeriod}
                  onChange={(sel) => {
                    setServicePeriod(sel);
                    handleServiceOrAmenitiesSelected();
                  }}
                  isDisabled={!servicePeriodOptions.length}
                  placeholder="Select Service Period"
                  styles={config.mode === 'dark' ? reactSelectDark : reactSelectLight}
                />
                {errors.servicePeriod && <div className="text-danger small">{errors.servicePeriod}</div>}
              </Col>

              <Col lg={4} md={6} sm={12} className="mb-3 mb-md-0">
                <Form.Label>Preference Amenities</Form.Label>
                <Select
                  isMulti
                  options={amenitiesOptions}
                  value={amenities}
                  onChange={(sel) => {
                    setAmenities(sel);
                    handleServiceOrAmenitiesSelected();
                  }}
                  isDisabled={!amenitiesOptions.length}
                  placeholder="Select Amenities"
                  styles={config.mode === 'dark' ? reactSelectDark : reactSelectLight}
                />
                {/* {errors.amenities && <div className="text-danger small">{errors.amenities}</div>} */}
              </Col>

              {bookingData?.mode_of_booking !== 'online' && (
                <Col lg={4} md={12} sm={12} className="mt-3 mt-lg-4">
                  <Form.Label>
                    Mode of Booking <span className="color-danger">*</span>
                  </Form.Label>
                  <div className="d-flex gap-4 align-items-center flex-wrap">
                    <Form.Check
                      type="radio"
                      label="Manual"
                      value="manual"
                      checked={modeOfBooking === 'manual'}
                      onChange={(e) => setModeOfBooking(e.target.value)}
                    />
                    <Form.Check
                      type="radio"
                      label="Phone Call"
                      value="phone"
                      checked={modeOfBooking === 'phone'}
                      onChange={(e) => setModeOfBooking(e.target.value)}
                    />
                  </div>
                  {errors.modeOfBooking && <div className="text-danger small">{errors.modeOfBooking}</div>}
                </Col>
              )}
            </Row>

            {timeSlots.length > 0 && (
              <Row className="mb-3">
                <Col xs={12}>
                  <Form.Label>
                    Time Slots <span className="color-danger">*</span>
                  </Form.Label>

                  <TimeSlotSlider
                    slots={timeSlots}
                    selected={timeSlot}
                    onSelect={(slotValue) => {
                      const slotObj = timeSlots.find((s) => s.value === slotValue);

                      setTimeSlot(slotValue);
                      setSelectedSlotMeta(slotObj);
                      setIsSynced(false);

                      const guestsNum = Number(guestCount);
                      const maxAllowed = isSynced ? slotObj.availableSeats : slotObj.availableSeats + originalGuestCount;

                      if (guestsNum <= maxAllowed) {
                        setErrors((e) => ({ ...e, guestCount: undefined }));
                      }
                    }}
                  />

                  {errors.timeSlot && <div className="text-danger small">{errors.timeSlot}</div>}
                </Col>
              </Row>
            )}

            <Row className="mb-3">
              <Col lg={4} md={6} sm={12} className="mb-3 mb-md-0">
                <Form.Label>
                  Customer Name <span className="color-danger">*</span>
                </Form.Label>
                <Form.Control value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
                {errors.customerName && <div className="text-danger small">{errors.customerName}</div>}
              </Col>

              <Col lg={4} md={6} sm={12} className="mb-3 mb-md-0">
                <Form.Label>
                  Customer Phone <span className="color-danger">*</span>
                </Form.Label>
                <div className="d-flex">
                  <span className="input-group-text">+61</span>
                  <Form.Control
                    value={customerPhone}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                      setCustomerPhone(value);
                    }}
                    placeholder="Enter phone number"
                  />
                </div>
                {errors.customerPhone && <div className="text-danger small">{errors.customerPhone}</div>}
              </Col>

              <Col lg={4} md={12} sm={12}>
                <Form.Label>Customer Email</Form.Label>
                <Form.Control value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} />
                {errors.customerEmail && <div className="text-danger small">{errors.customerEmail}</div>}
              </Col>
            </Row>

            <Row>
              <Col xs={12}>
                <Form.Label>Admin Comment</Form.Label>
                <Form.Control as="textarea" rows={3} value={adminComment} onChange={(e) => setAdminComment(e.target.value)} />
              </Col>
            </Row>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={() => onHide(false)} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || isLoading}>
          {saving ? (
            <><Spinner size="sm" className="me-2" />Saving...</>
          ) : (
            'Update Booking'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
